import { Alert, AlertTitle, Container } from '@mui/material';
import classnames from 'classnames';
import { useCallback } from 'react';
import ContentLoader from 'react-content-loader';
import { Helmet } from 'react-helmet';

import { NOT_FOUND } from '../../../shared/errors/errorMessage';
import Flex from '../../components/generic/Flex';
import { LoadingBoundary } from '../../components/generic/LoadingBoundary';
import Text from '../../components/generic/Text';
import { defaultLogoPath } from '../../components/settings/organization/portal/hooks/usePortalSettings';
import useAsyncRequest from '../../hooks/useAsyncRequest';
import COLORS from '../../theme/colors';
import { getPortalHeader } from '../api/organization';
import useSlug from '../hooks/useSlug';
import type { PortalHeader as PortalHeaderResponse } from '../../../shared/publicPortal/types';
import type { LoadingBoundaryProps } from '../../components/generic/LoadingBoundary';

import UserButton from './UserButton';

import styles from './Header.module.less';

const HeaderLoader = () => (
  <ContentLoader
    speed={2}
    width={500}
    height={94}
    viewBox="0 0 500 94"
    foregroundColor={COLORS.fossaGray05}
    backgroundColor={COLORS.fossaGray10}
  >
    <rect x="0" y="7" rx="3" ry="3" width="80" height="80" />
    <rect x="104" y="20" rx="3" ry="3" width="244" height="33" />
    <rect x="104" y="63" rx="3" ry="3" width="396" height="21" />
  </ContentLoader>
);

const HeaderError: LoadingBoundaryProps<PortalHeaderResponse>['renderError'] = ({ errors }) => {
  if (errors.length === 1 && errors[0] === NOT_FOUND) {
    return <HeaderLoader />;
  }

  return (
    <Container>
      <Alert severity="error">
        <AlertTitle>Uh oh!</AlertTitle>
        {errors.join(',')}
      </Alert>
    </Container>
  );
};

const HeaderContent: LoadingBoundaryProps<PortalHeaderResponse>['renderValue'] = ({ value }) => {
  const logoUrl = value.logoUrl ?? defaultLogoPath;

  return (
    <Flex>
      <Helmet>
        <title>{value.title}</title>
      </Helmet>
      <img
        className={styles.logo}
        src={logoUrl}
        alt="logo"
        onError={(e) => {
          // eslint-disable-next-line no-param-reassign
          e.currentTarget.className = classnames(styles.logo, 'hidden');
        }}
      />
      <hgroup className={styles.title}>
        <Text.H2>{value.title}</Text.H2>
        <p>{value.description}</p>
      </hgroup>
    </Flex>
  );
};

const Header = () => {
  const slug = useSlug();
  const getPortalReq = useCallback(() => getPortalHeader(slug), [slug]);
  const portalValues = useAsyncRequest(getPortalReq);

  return (
    <header className={styles.header}>
      <LoadingBoundary
        asyncValue={portalValues}
        renderValue={HeaderContent}
        renderLoading={<HeaderLoader />}
        renderEmpty={<HeaderLoader />}
        renderError={HeaderError}
      />
      <UserButton />
    </header>
  );
};

export default Header;
